import { ResultItem } from '../resultItem/ResultItem';
import { Pagination } from '../pagination/Pagination';
import { SkeletonResultList } from '../skeletonResultList/SkeletonResultList';
import type { Filters, ResultDataStats, ResultItem as ResultItemType } from '@/domain';
import { SortDetails } from 'shared/page/SortDetails';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import { insertBanners, useAdPositionElements } from '@schibsted-nmp/advertising-companion';

export interface ResultListProps {
    stats: ResultDataStats;
    filters: Filters;
    onFilterChanged: (filter: Filters) => void;
    isMobile: boolean;
    numberOfPreloadedImages: number;
    results: ResultItemType[] | false | null;
    loginId: number;
    loading?: boolean;
}

export const ResultList = ({
    stats,
    filters,
    onFilterChanged,
    isMobile,
    numberOfPreloadedImages,
    results,
    loading = false,
    loginId,
}: ResultListProps) => {
    const [bannerPositions, bannerElements] = useAdPositionElements({
        layoutType: 'grid',
    });

    const renderItemNodes = () =>
        (results as ResultItemType[]).map((item, index) => (
            <ResultItem
                key={item.id}
                index={index}
                result={item}
                filters={filters}
                imagePreloaded={index < numberOfPreloadedImages}
                loginId={loginId}
                isMobile={isMobile}
            />
        ));

    const renderItemsAndPagination = () => {
        const items = renderItemNodes();
        const adItemsWithBanners = insertBanners({
            adItems: items,
            bannerPositions,
            bannerElements,
        });

        return (
            <>
                <SortDetails section="feriehus" className="mb-16" />
                <section
                    aria-labelledby="ads-heading"
                    id="ads"
                    className="grid grid-flow-row-dense md:grid-cols-2 gap-16"
                    data-testid="resultList">
                    <h1 className="sr-only" id="ads-heading">
                        Søkeresultater
                    </h1>
                    {adItemsWithBanners}
                </section>
                <Pagination stats={stats} onFilterChanged={onFilterChanged} />
            </>
        );
    };

    const renderFetchFailedMessage = () => (
        <p className="p-16 text-22 font-bold text-center" data-testid="fetchFailed">
            {/* biome-ignore lint/a11y/useValidAnchor: This existed before biome was added */}
            Noe gikk galt under henting av tilbud. <a href="">Prøv å laste siden på nytt</a>.
        </p>
    );

    const renderNoResultsMessage = () => (
        <p className="p-16 text-22 font-bold text-center" data-testid="emptyResultList">
            {loading ? (
                <SpinnerIcon data-testid="newSearchSpinner" size={24} className="inline" />
            ) : (
                'Ingen treff akkurat nå. Zoom ut i kartet for å få flere treff.'
            )}
        </p>
    );

    if (results === null) {
        return <SkeletonResultList isMobile={isMobile} />;
    }

    if (!results) {
        return renderFetchFailedMessage();
    }

    if (results.length === 0) {
        return renderNoResultsMessage();
    }

    return renderItemsAndPagination();
};
